import {parseUnifiedDiff} from '@github-ui/agent-sessions/utils/parse-unified-diff'
import {CopilotCodeReviewFeedback} from '@github-ui/copilot-code-review-feedback/CopilotCodeReviewFeedback'
import {MarkdownRenderer} from '@github-ui/copilot-markdown'
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {CheckIcon, ChevronDownIcon, ChevronRightIcon, DashIcon, TriangleDownIcon, XIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, ButtonGroup, Link} from '@primer/react'
import {UnderlinePanels} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {type FC, type JSX, type MouseEvent, useMemo} from 'react'
import {
  getDisplayFilePath,
  getFindingRenderableFileLocation,
  getFindingSourceContextLinks,
  getFindingSources,
  getFindingTitle,
} from './entity-helpers'
import styles from './Finding.module.css'
import {FindingCommentPreview} from './FindingCommentPreview'
import {FixFindingButton, FixTaskExpandedStatus, FixTaskHeaderStatus} from './FixFindingStatus'
import type {CommentPreviewData, FindingResolvedReason, Finding as FindingType} from './types'
import {useFixTask} from './use-fix-task'

const RESOLVE_REASONS: Array<{
  reason: FindingResolvedReason
  label: string
  description: string
  icon: JSX.Element
}> = [
  {
    reason: 'addressed',
    label: 'Addressed',
    icon: <CheckIcon />,
    description: 'This finding has been addressed',
  },
  {
    reason: 'wont_fix',
    label: "Won't fix",
    icon: <XIcon />,
    description: "This finding won't be addressed",
  },
  {
    reason: 'incorrect',
    label: 'Incorrect',
    icon: <DashIcon />,
    description: 'This finding is incorrect or unhelpful',
  },
]

interface FindingProps {
  finding: FindingType
  displayFilePath?: string
  resolved: boolean
  isExpanded: boolean
  ccaTaskId?: string
  feedbackPath: string
  taskBasePath: string
  onToggleExpanded: () => void
  onDismiss: (resolvedReason: FindingResolvedReason) => void
  onReopen: () => void
  onFix: () => void
}

export const Finding: FC<FindingProps> = ({
  finding,
  displayFilePath,
  resolved,
  isExpanded,
  ccaTaskId,
  feedbackPath,
  taskBasePath,
  onToggleExpanded,
  onDismiss,
  onReopen,
  onFix,
}) => {
  const {taskData, fixTask, taskUrl} = useFixTask({ccaTaskId, taskBasePath})

  const sources = getFindingSources(finding)
  const fileLocation = getFindingRenderableFileLocation(finding, displayFilePath)
  const sourceContextLinks = fileLocation ? [] : getFindingSourceContextLinks(finding)
  const title = getFindingTitle(finding)
  const shortenedFilePath = displayFilePath ?? getDisplayFilePath(finding.filePath)
  const commentPreviews = finding.commentPreviews ?? []
  const fileLocationFullPathText =
    fileLocation && fileLocation.fullLocation !== fileLocation.displayLocation ? (
      <span className="sr-only">{` Full path: ${fileLocation.fullLocation}`}</span>
    ) : null
  const shortenedFilePathFullPathText =
    fileLocation && finding.filePath && shortenedFilePath !== finding.filePath ? (
      <span className="sr-only">{` Full path: ${finding.filePath}`}</span>
    ) : null

  const diffLines = useMemo(
    () => (finding.suggestion ? parseUnifiedDiff(finding.suggestion) : []),
    [finding.suggestion],
  )
  const resolvedReasonLabel = finding.resolvedReason
    ? RESOLVE_REASONS.find(({reason}) => reason === finding.resolvedReason)?.label
    : undefined
  const resolvedLabel = resolvedReasonLabel ? `Resolved as ${resolvedReasonLabel}` : undefined

  const handleHeaderClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target instanceof Element && e.target.closest('a')) return
    onToggleExpanded()
  }

  return (
    <li className={styles.Finding}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- keyboard a11y is handled by the nested toggle button */}
      <div className={styles.header} onClick={handleHeaderClick}>
        <button
          type="button"
          className={styles.toggleButton}
          aria-expanded={isExpanded}
          aria-label={`Details for: ${title}`}
        >
          {isExpanded ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
        </button>
        <div className={styles.headerContent}>
          <span className="text-semibold color-fg-default">{title}</span>
          <span className="d-flex flex-items-center flex-wrap gap-1 color-fg-muted text-small">
            {sources.map(source => (
              <span key={source.key} className={clsx(styles.metaItem, styles.sourceItem)}>
                {source.author?.avatarUrl && (
                  <GitHubAvatar
                    alt={source.url ? '' : `@${source.author.login}`}
                    size={20}
                    src={source.author.avatarUrl}
                    square={source.author.isBot}
                  />
                )}
                {source.url ? (
                  <Link href={source.url} className="Link--inTextBlock">
                    {source.label}
                    {source.author && <span className="sr-only"> by @{source.author.login}</span>}
                  </Link>
                ) : (
                  <span>{source.label}</span>
                )}
              </span>
            ))}
            {fileLocation && (
              <span className={styles.metaItem}>
                <Link href={fileLocation.url} className="text-mono Link--inTextBlock">
                  <span>{fileLocation.displayLocation}</span>
                  {fileLocationFullPathText}
                </Link>
              </span>
            )}
            {sourceContextLinks.map(link => (
              <span key={link.url} className={styles.metaItem}>
                <Link href={link.url} className="Link--inTextBlock">
                  {link.label}
                </Link>
              </span>
            ))}
            {fixTask && (
              <span className={styles.metaItem}>
                <FixTaskHeaderStatus fixTask={fixTask} taskUrl={taskUrl} />
              </span>
            )}
          </span>
        </div>
      </div>
      {isExpanded && (
        <div className={styles.expandedContent}>
          <div className={styles.section}>
            <MarkdownRenderer wrapCodeLines className="tmp-mb-3" markdown={finding.message} />
          </div>
          <div>
            {commentPreviews.length > 1 && (
              <UnderlinePanels aria-label="Comment previews" className="tmp-px-0">
                {commentPreviews.map((preview, index) => (
                  <UnderlinePanels.Tab key={`tab-${preview.commentDatabaseId}`} aria-selected={index === 0}>
                    <div className="d-flex flex-items-center">
                      <GitHubAvatar
                        size={16}
                        className="tmp-mr-1"
                        square={preview.author.isBot}
                        src={preview.author.avatarUrl}
                        alt=""
                      />
                      {preview.author.login}
                    </div>
                  </UnderlinePanels.Tab>
                ))}
                {commentPreviews.map((preview, index) => (
                  <UnderlinePanels.Panel key={`panel-${preview.commentDatabaseId}`} hidden={index !== 0}>
                    <FindingCommentPreview preview={preview} filePath={fileLocation ? finding.filePath : undefined} />
                  </UnderlinePanels.Panel>
                ))}
              </UnderlinePanels>
            )}
            {commentPreviews.length === 1 && (
              <FindingCommentPreview
                preview={commentPreviews[0] as CommentPreviewData}
                filePath={fileLocation ? finding.filePath : undefined}
                hideMessage
              />
            )}
            {commentPreviews.length === 0 && diffLines.length > 0 && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Suggestions</h3>
                <div className={styles.diffPreview}>
                  <div className={clsx('d-flex flex-column overflow-hidden', styles.diffEntryHeader)}>
                    {fileLocation && (
                      <div className="d-flex flex-items-center tmp-px-3 py-2 text-small gap-2 text-mono">
                        <span>
                          <span>{shortenedFilePath}</span>
                          {shortenedFilePathFullPathText}
                        </span>
                      </div>
                    )}
                    <StaticUnifiedDiffPreview
                      tabSize={4}
                      className={clsx(
                        'm-0 border-bottom borderColor-default overflow-hidden',
                        styles.automatedReviewDiff,
                      )}
                      diffTableClassName="borderColor-default"
                      diffLines={diffLines}
                    />
                  </div>
                </div>
              </div>
            )}
            {commentPreviews.length === 0 && diffLines.length === 0 && finding.suggestion && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Suggestion</h3>
                <pre className="color-bg-subtle tmp-p-3 rounded-2 overflow-auto text-small">{finding.suggestion}</pre>
              </div>
            )}
          </div>
          {fixTask && <FixTaskExpandedStatus fixTask={fixTask} taskData={taskData} taskUrl={taskUrl} />}
          <div className="d-flex flex-items-center flex-justify-between tmp-mt-3">
            <CopilotCodeReviewFeedback
              commentId={finding.id}
              feedbackPath={feedbackPath}
              showNegativeFeedbackModal={false}
              additionalParameters={{finding_id: finding.id, feedback_target: 'FINDING'}}
            />
            <div className="d-flex flex-items-center gap-2 ml-auto">
              <FixFindingButton resolved={resolved} fixTask={fixTask} onFix={onFix} />
              {resolved ? (
                <>
                  {resolvedLabel && <span className="text-small color-fg-muted">{resolvedLabel}</span>}
                  <Button onClick={onReopen}>Reopen</Button>
                </>
              ) : (
                <ButtonGroup>
                  <Button onClick={() => onDismiss('addressed')}>Resolve</Button>
                  <ActionMenu>
                    <ActionMenu.Button aria-label="Resolve options" icon={TriangleDownIcon} />
                    <ActionMenu.Overlay width="medium" align="end">
                      <ActionList>
                        {RESOLVE_REASONS.map(({reason, label, description, icon}) => (
                          <ActionList.Item key={reason} onSelect={() => onDismiss(reason)}>
                            <ActionList.LeadingVisual>{icon}</ActionList.LeadingVisual>
                            {label}
                            <ActionList.Description variant="block">{description}</ActionList.Description>
                          </ActionList.Item>
                        ))}
                      </ActionList>
                    </ActionMenu.Overlay>
                  </ActionMenu>
                </ButtonGroup>
              )}
            </div>
          </div>
        </div>
      )}
    </li>
  )
}
